/**
 * T1228 — enotna razlaga, zakaj je kontrolnik zaklenjen.
 *
 * Od T1228 zaledje uporabnika brez plačanega sedeža zniža na vlogo `viewer`
 * (`resolve_project_access`), zato mu izvoz, komentar in izbira cenovnega
 * scenarija vrnejo 403. Sam 403 se v vmesniku bere kot okvara platforme — zato
 * se vsak tak kontrolnik degradira **z razlago**, nikoli s tiho odsotnostjo
 * (spec §5.4).
 *
 * Razlogi so **dva** in nista zamenljiva:
 *
 * - **brez sedeža** → »Za to potrebujete sedež; obrnite se na lastnika
 *   organizacije«. Rešitev je pri lastniku organizacije (dodelitev na
 *   `/members`), ne pri vodji projekta;
 * - **s sedežem, a vloga opazovalec** → rešitev je pri vodji projekta.
 *
 * Zato razlago izbere `has_seat` iz `moduleStore` (polje `me/modules/`), sam
 * *pogoj* za degradacijo pa ostane projektna vloga: `my_role` že nosi sedežni
 * strop (`ProjectSerializer._cap_role_by_seat`), tako da vmesnik in zaledje
 * ne moreta razsoditi različno.
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useModuleStore } from '@/store/moduleStore';
import { useOrganizationStore } from '@/store/organizationStore';
import { cn } from '@/lib/utils';

/** Modul, pod katerim živi popis (BoQ) — edini, ki ga ta razlaga danes pokriva. */
export const QUANTIFY_MODULE = 'quantify';

/**
 * T1929 — ali sme bralec sedeže deliti sam.
 *
 * Po odločitvi **T1228** sedeže deli lastnik **ali** administrator
 * organizacije, sama administracija pa sedeža ne zahteva. Brezsedežni lastnik
 * si ga torej lahko dodeli sam — in prav njemu je razlaga dotlej naročala, naj
 * se obrne na lastnika organizacije, torej nase (izmerjeno pri **V0444**).
 */
function useCanManageSeats(): boolean {
  const role = useOrganizationStore((s) => s.currentOrganization?.role);
  return role === 'owner' || role === 'admin';
}

/**
 * T2448 — ali organizaciji modul **ni aktiven** (`offered`, `trial_expired`, …).
 *
 * Takrat zaledje zavrne že na `@require_feature(<modul>)`, sedež pa ni vzrok:
 * pri neaktivnem modulu ga ni mogoče dodeliti in ne bi pomagal. Napotek
 * »dodelite si sedež v razdelku Ekipa« bi uporabnika poslal v slepo ulico.
 *
 * Neznano ni isto kot neaktivno: modul, ki ga v `moduleStore` ni (seznam še ni
 * naložen ali poizvedba ni uspela), tu vrne `false` — enako previdno kot
 * `hasSeat()`, ki neznano bere kot »sedež ima«.
 */
export function useModuleInactive(moduleSlug: string = QUANTIFY_MODULE): boolean {
  return useModuleStore(
    (s) => moduleSlug in s.modules && !s.hasModule(moduleSlug),
  );
}

/** T2484 — povezava ob razlagi (npr. na Naročnino). */
export interface SeatGateAction {
  /** Prevedeno besedilo povezave. */
  label: string;
  /** Pot v aplikaciji (`react-router`). */
  to: string;
}

/** Pot do Naročnine (`App.tsx`: `<Route path="/billing">`). */
const BILLING_PATH = '/billing';

/**
 * T2484 — povezava na Naročnino ob neaktivnem modulu, **samo** za tistega, ki
 * Naročnino ureja (lastnik, administrator). Navaden član je napoten k lastniku
 * in povezave ne dobi; ob aktivnem ali neznanem stanju modula je ni.
 *
 * Za klicatelje, ki razlago podajo sami (`message`) in so izbrali razlago o
 * neaktivnem modulu (`useSeatMissingMessage`) — npr. Formati kode.
 */
export function useModuleInactiveAction(
  moduleSlug: string = QUANTIFY_MODULE,
): SeatGateAction | undefined {
  const { t } = useTranslation();
  const moduleInactive = useModuleInactive(moduleSlug);
  const canManageSeats = useCanManageSeats();
  if (!moduleInactive || !canManageSeats) return undefined;
  return { to: BILLING_PATH, label: t('seats.gate_open_billing') };
}

/** Ključ razloga »manjka sedež« — ali, kadar modul ni aktiven, »modul ni aktiven«. */
function missingSeatKey(moduleInactive: boolean, canManageSeats: boolean): string {
  if (moduleInactive) {
    return canManageSeats ? 'seats.gate_module_inactive_self' : 'seats.gate_module_inactive';
  }
  return canManageSeats ? 'seats.gate_needed_self' : 'seats.gate_needed';
}

/**
 * Ključ razloga — **tri** možnosti, ne dve.
 *
 * Meniji potrebujejo ključ in ne prevedenega niza (`sectionMenuModel`,
 * `itemMenuModel`), zato je izbira tu in ne v `useSeatGateMessage`.
 */
export function useSeatGateKey(moduleSlug: string = QUANTIFY_MODULE): string {
  const hasSeat = useModuleStore((s) => s.hasSeat(moduleSlug));
  const moduleInactive = useModuleInactive(moduleSlug);
  const canManageSeats = useCanManageSeats();
  if (hasSeat) return 'seats.gate_viewer';
  return missingSeatKey(moduleInactive, canManageSeats);
}

/** Polni stavek razloga — primeren za `title`, `aria-label` ali telo obvestila. */
export function useSeatGateMessage(moduleSlug: string = QUANTIFY_MODULE): string {
  const { t } = useTranslation();
  return t(useSeatGateKey(moduleSlug));
}

/**
 * T1929 — razlog »manjka sedež« s pravim naslovnikom, brez vlogovne veje.
 *
 * Za klicatelje, ki odsotnost sedeža ugotovijo sami in imajo za primer
 * »sedež ima« svojo, drugačno razlago — izvoz nadzorne plošče zahteva
 * `manager` (T1515), predloge oblike kode pa `IsOrganizationAdmin` (T1233).
 * Oba sta stavek dotlej sestavljala sama in sta zato ostala pri starem
 * naslovniku; pri drugem je bila veja dosegljiva **samo** administratorju
 * organizacije, torej človeku, ki sedeže deli.
 *
 * T2448: kadar modul organizaciji ni aktiven, sedež ni vzrok — razlaga tedaj
 * napoti na Naročnino (lastnik/administrator) oziroma k lastniku (član).
 */
export function useSeatMissingMessage(moduleSlug: string = QUANTIFY_MODULE): string {
  const { t } = useTranslation();
  const moduleInactive = useModuleInactive(moduleSlug);
  const canManageSeats = useCanManageSeats();
  return t(missingSeatKey(moduleInactive, canManageSeats));
}

/**
 * T1929 — prevod razloga, ki pride iz čistega menijskega modela.
 *
 * `sectionMenuModel`/`itemMenuModel` vlog ne vidita in vračata `titleKey`,
 * zato naslovnika ni mogoče izbrati tam; izbere se ob izrisu. Zamenjava je
 * namerno **ozka** — samo `seats.gate_needed` — da popravek ne povozi
 * vlogovnih in zaklepnih razlag, ki imajo svojega naslovnika.
 */
export function useGateTitle(
  moduleSlug: string = QUANTIFY_MODULE,
): (key?: string) => string | undefined {
  const { t } = useTranslation();
  const moduleInactive = useModuleInactive(moduleSlug);
  const canManageSeats = useCanManageSeats();
  return (key?: string) => {
    if (!key) return undefined;
    return t(key === 'seats.gate_needed' ? missingSeatKey(moduleInactive, canManageSeats) : key);
  };
}

/** Kratka oznaka za tesne prostore (orodna vrstica); polni stavek nosi `title`. */
export function useSeatGateLabel(moduleSlug: string = QUANTIFY_MODULE): string {
  const { t } = useTranslation();
  const hasSeat = useModuleStore((s) => s.hasSeat(moduleSlug));
  const moduleInactive = useModuleInactive(moduleSlug);
  if (hasSeat) return t('seats.gate_short_viewer');
  // T2448: pri neaktivnem modulu »Potreben sedež« ni res — polni stavek v `title` to pojasni.
  return moduleInactive ? t('seats.gate_short_module_inactive') : t('seats.gate_short');
}

interface NoticeProps {
  moduleSlug?: string;
  className?: string;
}

interface BlockNoticeProps extends NoticeProps {
  /**
   * T1233 (rep): izrecna razlaga namesto privzete sedežne.
   *
   * Privzeta razlaga izbira med »manjka sedež« in »vloga je opazovalec« —
   * druga od teh napoti uporabnika k **vodji projekta**. Za organizacijske
   * nastavitve (predloge oblike kode) je to napačen naslov: prag ni projektna
   * vloga, ampak `IsOrganizationAdmin`. Zato sme klicatelj podati svoj stavek;
   * videz, `role="note"` in `data-testid` ostanejo isti, da se vzorec
   * »degradacija z razlago« ne razveji v drugo obliko.
   */
  message?: string;
  /**
   * T2484: neobvezna povezava za razlago (npr. na Naročnino). Brez `message`
   * jo obvestilo pri neaktivnem modulu doda samo (`useModuleInactiveAction`);
   * izrecnemu sporočilu je samodejno ne pripne, ker ne ve, o čem govori.
   */
  action?: SeatGateAction;
}

/**
 * Blokovno obvestilo — uporabi tam, kjer je prostor za cel stavek (panel
 * komentarjev, vsebina spustnega menija).
 */
export function SeatGateNotice({
  moduleSlug = QUANTIFY_MODULE,
  className,
  message: override,
  action,
}: BlockNoticeProps) {
  const seatMessage = useSeatGateMessage(moduleSlug);
  const seatKey = useSeatGateKey(moduleSlug);
  const inactiveAction = useModuleInactiveAction(moduleSlug);
  const message = override ?? seatMessage;
  // T2484: privzeta razlaga o neaktivnem modulu dobi povezavo samodejno — a le
  // takrat, ko je izbrana prav ta razlaga (sedež ima prednost, gl. useSeatGateKey).
  const resolvedAction =
    action ??
    (override === undefined && seatKey === 'seats.gate_module_inactive_self'
      ? inactiveAction
      : undefined);
  return (
    <div
      role="note"
      data-testid="seat-gate-notice"
      className={cn(
        'flex items-start gap-2 rounded-lg border px-3 py-2 text-caption leading-relaxed',
        'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
        'border-amber-100 dark:border-amber-800',
        className,
      )}
    >
      <Lock className="h-3.5 w-3.5 shrink-0 mt-px" aria-hidden="true" />
      <span>
        {message}
        {resolvedAction && (
          <>
            {' '}
            <Link
              to={resolvedAction.to}
              data-testid="seat-gate-notice-action"
              className="font-semibold underline underline-offset-2 hover:no-underline"
            >
              {resolvedAction.label}
            </Link>
          </>
        )}
      </span>
    </div>
  );
}

/**
 * Ključavnica s kratko oznako — nadomesti gumb tam, kjer cel stavek ne gre
 * (orodna vrstica BoQ). Polni stavek je v `title` **in** v skritem besedilu za
 * bralnike zaslona, da razlaga ne visi samo na miškinem prehodu.
 */
export function SeatGateChip({
  moduleSlug = QUANTIFY_MODULE,
  className,
  hideLabel = false,
}: NoticeProps & {
  /** T2499: orodna vrstica ob pomanjkanju prostora pusti samo ključavnico. */
  hideLabel?: boolean;
}) {
  const message = useSeatGateMessage(moduleSlug);
  const label = useSeatGateLabel(moduleSlug);
  return (
    <span
      role="note"
      data-testid="seat-gate-chip"
      title={message}
      className={cn(
        'flex items-center gap-1.5 px-3 py-[7px] text-dense font-medium rounded-lg cursor-not-allowed',
        'bg-[var(--bp-surface-2)] border border-[var(--bp-border-soft)] text-[var(--bp-mute)]',
        className,
      )}
    >
      <Lock className="h-3.5 w-3.5" aria-hidden="true" />
      {!hideLabel && <span className="hidden lg:inline">{label}</span>}
      <span className="sr-only">{message}</span>
    </span>
  );
}
